import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  CalendarDays, Check, Copy, ExternalLink, ListChecks, Maximize2, Minus, Plus,
  Presentation, Share2, Users
} from 'lucide-react';
import Modal from '../common/Modal';
import Loader from '../common/Loader';
import { FormField } from '../common/FormField';
import { useLanguage } from '../../assets/js/LanguageContext';
import '../../assets/styles/FormCard.css';
import '../../assets/styles/DashboardModals.css';
import { ratingScale } from '../common-questions/TRating';

const VIOLET = '#7C3AED';

export const parseParticipants = (value) => {
  const [done, invited] = String(value ?? '0').split('/');
  return { done: parseInt(done, 10) || 0, invited: parseInt(invited, 10) || 0 };
};

// "3 de 24 respuestas". Si responde mas gente de la invitada (el PIN se puede reenviar)
// no se muestra "25 de 24": "25 respuestas · 24 invitados".
export const responsesText = (t, done, invited) => {
  const count = t(done === 1 ? 'dash_responses_one' : 'dash_responses_count').replace('{count}', done);
  if (invited <= 0) return count;
  if (done <= invited) return t('dash_responses_of').replace('{done}', done).replace('{total}', invited);
  return `${count} · ${t(invited === 1 ? 'dash_invited_one' : 'dash_invited_count').replace('{count}', invited)}`;
};

const surveyUrl = (pin) => `${process.env.REACT_APP_APP_URL}/${pin}`;
const displayUrl = (url) => url.replace(/^https?:\/\//, '');

const useCopy = () => {
  const [copied, setCopied] = useState(null);
  const copy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied((current) => (current === key ? null : current)), 1600);
    } catch (error) {
      console.error('No se pudo copiar:', error);
    }
  };
  return [copied, copy];
};

const CopyButton = ({ copied, onClick, label, copiedLabel }) => (
  <button type="button" className="tp-dmod__copy-btn" onClick={onClick}>
    {copied ? <Check size={15} strokeWidth={2.5} aria-hidden="true" /> : <Copy size={15} strokeWidth={2} aria-hidden="true" />}
    <span>{copied ? copiedLabel : label}</span>
  </button>
);

const closeHandler = (onClose) => (open) => {
  if (!open) onClose();
};

export const ShareSurveyModal = ({ survey, onClose }) => {
  const { t } = useLanguage();
  const [copied, copy] = useCopy();
  if (!survey) return null;

  const url = surveyUrl(survey.pin);
  const message = `${t('dash_share_hello')}

${t('dash_share_enter_link')}

thepul.se

${t('dash_share_enter_code')}

${survey.pin}

${t('dash_share_long_text')}

${t('dash_share_thanks')}`;

  return (
    <Modal
      open
      onOpenChange={closeHandler(onClose)}
      size="md"
      icon={<Share2 size={18} strokeWidth={2} />}
      tone={VIOLET}
      title={t('dash_share_survey')}
      description={t('dmod_share_desc')}
      cancelText={t('dash_close')}
      confirmText={copied === 'message' ? t('dash_copied') : t('dash_copy_text')}
      onConfirm={() => {
        copy('message', message);
        return false;
      }}
    >
      <div className="tp-dmod__copy-rows">
        <div className="tp-dmod__copy-row">
          <span className="tp-dmod__copy-label">{t('dash_th_pin')}</span>
          <strong className="tp-dmod__copy-value tp-dmod__copy-value--pin">{survey.pin}</strong>
          <CopyButton
            copied={copied === 'pin'}
            onClick={() => copy('pin', String(survey.pin))}
            label={t('dmod_copy')}
            copiedLabel={t('dmod_copied')}
          />
        </div>
        <div className="tp-dmod__copy-row">
          <span className="tp-dmod__copy-label">{t('dmod_link')}</span>
          <span className="tp-dmod__copy-value">{displayUrl(url)}</span>
          <CopyButton
            copied={copied === 'link'}
            onClick={() => copy('link', url)}
            label={t('dmod_copy')}
            copiedLabel={t('dmod_copied')}
          />
        </div>
      </div>

      <label className="tp-dmod__label" htmlFor="share-message">{t('dmod_message')}</label>
      <textarea id="share-message" className="tp-dmod__message" readOnly value={message} rows={9} />
    </Modal>
  );
};

export const QuestionsModal = ({ survey, loading, error, questions = [], onClose }) => {
  const { t } = useLanguage();
  if (!survey) return null;

  const typeLabel = (tipo) => {
    if (tipo === 'open') return t('question_type_open');
    if (tipo === 'closed') return t('question_type_closed');
    return t('dash_question_type_rating');
  };

  return (
    <Modal
      open
      onOpenChange={closeHandler(onClose)}
      size="lg"
      icon={<ListChecks size={18} strokeWidth={2} />}
      tone={VIOLET}
      title={t('dash_questions_modal_title')}
      description={survey.titulo}
      cancelText={t('dash_close')}
    >
      {loading && <Loader />}
      {!loading && error && <p className="tp-dmod__status is-error">{t('dash_questions_error')}</p>}
      {!loading && !error && questions.length === 0 && (
        <p className="tp-dmod__status">{t('dash_questions_empty')}</p>
      )}
      {!loading && !error && questions.length > 0 && (
        <ol className="tp-dmod__questions">
          {questions.map((q, index) => {
            const options = typeof q.opciones === 'string'
              ? q.opciones.split(',').map((o) => o.trim()).filter(Boolean)
              : Array.isArray(q.opciones) ? q.opciones : [];
            return (
              <li key={q.id ?? index} className="tp-dmod__question">
                <span className="tp-dmod__question-num" aria-hidden="true">{index + 1}</span>
                <div className="tp-dmod__question-body">
                  <div className="tp-dmod__question-head">
                    <span className="tp-dmod__question-text">{q.pregunta}</span>
                    <span className={`tp-dmod__type is-${q.tipo}`}>{typeLabel(q.tipo)}</span>
                  </div>
                  {q.tipo === 'closed' && options.length > 0 && (
                    <ul className="tp-dmod__options">
                      {options.map((option, i) => <li key={i}>{option}</li>)}
                    </ul>
                  )}
                  {q.tipo === 'rating' && (
                    <p className="tp-dmod__range">{ratingScale(q).min} – {ratingScale(q).max}</p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </Modal>
  );
};

export const PresentationModal = ({ survey, dateRange, onClose }) => {
  const { t } = useLanguage();
  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoom, setZoom] = useState(400);
  const [copied, copy] = useCopy();
  if (!survey) return null;

  const url = surveyUrl(survey.pin);
  const { done, invited } = parseParticipants(survey.participantes);

  return (
    <>
      <Modal
        open
        onOpenChange={closeHandler(onClose)}
        size="xl"
        className="tp-present"
        icon={<Presentation size={18} strokeWidth={2} />}
        tone={VIOLET}
        title={survey.titulo}
        description={t('dmod_present_desc')}
        cancelText={t('dash_close')}
      >
        <div className="tp-present__grid">
          <div className="tp-present__info">
            <div className="tp-present__pin">
              <span className="tp-present__pin-label">{t('dash_th_pin')}</span>
              <strong className="tp-present__pin-value">{survey.pin}</strong>
            </div>

            <dl className="tp-present__stats">
              <div className="tp-present__stat">
                <dt><Users size={16} strokeWidth={2} aria-hidden="true" />{t('dmod_responses_live')}</dt>
                <dd>
                  <span className="tp-present__live" aria-hidden="true" />
                  {invited > 0 && done <= invited ? `${done} / ${invited}` : done}
                </dd>
              </div>
              <div className="tp-present__stat">
                <dt><CalendarDays size={16} strokeWidth={2} aria-hidden="true" />{t('dmod_dates')}</dt>
                <dd>{dateRange}</dd>
              </div>
            </dl>
          </div>

          <div className="tp-present__qr">
            <p className="tp-present__qr-title">{t('dmod_scan')}</p>
            <button
              type="button"
              className="tp-present__qr-btn"
              onClick={() => setZoomOpen(true)}
              aria-label={t('dmod_enlarge_qr')}
              title={t('dmod_enlarge_qr')}
            >
              <QRCodeSVG value={url} size={220} level="M" includeMargin />
              <span className="tp-present__qr-zoom" aria-hidden="true"><Maximize2 size={14} strokeWidth={2.25} /></span>
            </button>
            <p className="tp-present__url">
              {t('dmod_or_enter')} <strong>{displayUrl(url)}</strong>
            </p>
            <div className="tp-present__actions">
              <button type="button" className="tp-dmod__copy-btn" onClick={() => window.open(url, '_blank', 'noopener')}>
                <ExternalLink size={15} strokeWidth={2} aria-hidden="true" />
                <span>{t('dmod_open')}</span>
              </button>
              <CopyButton
                copied={copied === 'link'}
                onClick={() => copy('link', url)}
                label={t('dmod_copy_link')}
                copiedLabel={t('dmod_copied')}
              />
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={zoomOpen}
        onOpenChange={setZoomOpen}
        size="xl"
        className="tp-present"
        title={t('dash_qr_expanded')}
        description={displayUrl(url)}
        cancelText={t('dash_close')}
      >
        <div className="tp-present__zoom">
          <div className="tp-present__zoom-qr">
            <QRCodeSVG value={url} size={zoom} level="M" includeMargin />
          </div>
          <div className="tp-present__zoom-controls">
            <button
              type="button"
              className="tp-dmod__icon-btn"
              onClick={() => setZoom((value) => Math.max(value - 50, 200))}
              disabled={zoom <= 200}
              aria-label={t('dmod_zoom_out')}
            >
              <Minus size={16} strokeWidth={2.25} />
            </button>
            <span className="tp-present__zoom-value">{zoom}px</span>
            <button
              type="button"
              className="tp-dmod__icon-btn"
              onClick={() => setZoom((value) => Math.min(value + 50, 800))}
              disabled={zoom >= 800}
              aria-label={t('dmod_zoom_in')}
            >
              <Plus size={16} strokeWidth={2.25} />
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export const DeleteSurveyModal = ({ open, pin, titulo, onClose, onDeleted }) => {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (open) {
      setBusy(false);
      setFailed(false);
    }
  }, [open, pin]);

  if (!open) return null;

  const handleDelete = async () => {
    setBusy(true);
    setFailed(false);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No se encontró el token de autenticación.');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/encuestas/${pin}/encuestaeliminar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ pincode: pin })
      });

      if (!response.ok) throw new Error(`Error al eliminar encuesta: ${response.status}`);

      onDeleted();
      onClose();
    } catch (error) {
      console.error('Error al eliminar la encuesta:', error);
      setFailed(true);
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onOpenChange={(isOpen) => {
        if (!isOpen && !busy) onClose();
      }}
      variant="delete"
      title={t('dmod_delete_title')}
      description={t('dmod_delete_desc').replace('{title}', titulo).replace('{pin}', pin)}
      cancelText={t('dash_cancel')}
      confirmText={busy ? t('dmod_deleting') : t('dash_delete')}
      confirmDisabled={busy}
      onConfirm={() => {
        handleDelete();
        return false;
      }}
    >
      {failed && <p className="tp-modal__error" role="alert">{t('dmod_delete_error')}</p>}
    </Modal>
  );
};

const toYMD = (date) => {
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().split('T')[0];
};

const fromYMD = (value) => {
  const [y, m, d] = String(value).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

export const EditSurveyModal = ({ survey, onClose, onSaved }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState(survey);
  const [errorText, setErrorText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(survey);
    setErrorText('');
    setSaving(false);
  }, [survey]);

  if (!survey || !form) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const todayYMD = toYMD(today);
  const minEnd = form.fecha_inicio > todayYMD ? form.fecha_inicio : todayYMD;
  // Un diagnostico que ya empezo conserva su fecha de inicio: solo se valida si se cambia
  const startChanged = form.fecha_inicio !== survey.fecha_inicio;
  const minStart = !startChanged && survey.fecha_inicio < toYMD(yesterday) ? survey.fecha_inicio : toYMD(yesterday);

  const update = (field) => (event) => {
    setForm({ ...form, [field]: event.target.value });
    setErrorText('');
  };

  const save = async () => {
    if (saving) return;
    if (!String(form.titulo || '').trim()) {
      setErrorText(t('dmod_title_required'));
      return;
    }
    const start = fromYMD(form.fecha_inicio);
    const end = fromYMD(form.fecha_fin);
    if (startChanged && start < today) {
      setErrorText(t('dash_alert_start_date_past'));
      return;
    }
    if (end < today) {
      setErrorText(t('dash_alert_end_date_past'));
      return;
    }
    if (end < start) {
      setErrorText(t('dash_alert_end_before_start'));
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/modencuesta/${form.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          titulo: form.titulo.trim(),
          fecha_inicio: form.fecha_inicio,
          fecha_fin: form.fecha_fin
        })
      });

      if (!res.ok) throw new Error(`Error al actualizar encuesta: ${res.status}`);
      onSaved();
      onClose();
    } catch (err) {
      console.error(err);
      setErrorText(t('dmod_edit_error'));
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onOpenChange={(isOpen) => {
        if (!isOpen && !saving) onClose();
      }}
      size="md"
      variant="edit"
      title={t('dash_edit_survey_title')}
      description={t('dmod_edit_desc')}
      cancelText={t('dash_cancel')}
      confirmText={saving ? t('dmod_saving') : t('dash_save')}
      confirmDisabled={saving}
      onConfirm={() => {
        save();
        return false;
      }}
    >
      <form
        className="tp-form tp-form--bare"
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
        noValidate
      >
        <FormField
          id="edit-survey-title"
          label={t('dmod_edit_title_label')}
          value={form.titulo}
          onChange={update('titulo')}
          required
        />
        <div className="tp-form__grid">
          <FormField
            id="edit-survey-start"
            type="date"
            label={t('dash_edit_start_date')}
            value={form.fecha_inicio}
            min={minStart}
            onChange={update('fecha_inicio')}
          />
          <FormField
            id="edit-survey-end"
            type="date"
            label={t('dash_edit_end_date')}
            value={form.fecha_fin}
            min={minEnd}
            onChange={update('fecha_fin')}
          />
        </div>
        {errorText && <p className="tp-form__alert" role="alert">{errorText}</p>}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  );
};
